/**
 * @fileoverview Request selection for the Sales Performance mock endpoints.
 * @description Validates a request's period and team the way the backend does (400/0001 for a bad
 * or not-yet-started period or team key, 403/4130 when a caller who may not filter sends a team,
 * 404/4131 for a team that names nobody selectable) and works out the people the selection covers,
 * with the ranking order of those people. Shared by the read replies and the export reply.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'

import { indiaToday } from './my-page-mock.service'
import { mockCaller } from './sales-crm-mock-projection'
import { failReply } from './sales-crm-mock-response.utils'
import {
  compareRanking,
  isSeller,
  peopleInScope,
  periodOf,
  scopeOf,
  targetsFor,
} from './sales-performance-mock-model'
import { listSalesRoster } from './sales-roster-mock-store'

import type { MockReply } from './sales-crm-mock-response.utils'
import type {
  SalesPerformancePeriod,
  SalesPerformancePeriodCode,
} from '@/features/sales/types/sales-performance.types'
import type { SalesUserRecord } from '@/features/sales/types/tenant.types'

export const PERIOD_CODES: readonly string[] = ['FY', 'Q1', 'Q2', 'Q3', 'Q4']
const TEAM_KEY = /^(ALL|(USER|TEAM):.+)$/

/** @description A request's validated selection: the period, and the people it covers. */
export interface Selection {
  period: SalesPerformancePeriod
  caller: SalesUserRecord
  people: SalesUserRecord[]
  today: string
}

/**
 * @description Validates the period and team of a request and works out who they cover.
 * @param {Record<string, unknown>} input - The query parameters or the export body.
 * @returns {Selection | MockReply} The selection, or the refusal to send.
 */
export function selectionOf(input: Record<string, unknown>): Selection | MockReply {
  const caller = mockCaller()
  if (!caller) return failReply(403, '2008', 'Not allowed')
  const today = indiaToday()
  const code = typeof input.period === 'string' ? input.period : 'FY'
  const team = typeof input.team === 'string' ? input.team : undefined
  if (!PERIOD_CODES.includes(code) || (team !== undefined && !TEAM_KEY.test(team))) {
    return failReply(400, '0001', 'Invalid request')
  }
  const { period, started } = periodOf(code as SalesPerformancePeriodCode, today)
  if (!started) return failReply(400, '0001', 'That quarter has not started')
  if (team !== undefined && !scopeOf(caller).canFilterTeam) {
    return failReply(403, '4130', 'The team filter is not available to this user')
  }
  const people = peopleFor(caller, team)
  return people ? { period, caller, people, today } : failReply(404, '4131', 'Team not found')
}

/**
 * @description Whether a validated-or-refused selection is the refusal to send.
 * @param {Selection | MockReply} value - The result of selectionOf.
 * @returns {boolean} True when it is a refusal.
 */
export function isReply(value: Selection | MockReply): value is MockReply {
  return Array.isArray(value)
}

/**
 * @description The people a team key selects within the caller's scope, or null when it names
 * nobody selectable.
 * @param {SalesUserRecord} caller - The signed-in user.
 * @param {string | undefined} team - `ALL`, `USER:id`, `TEAM:id` or nothing.
 * @returns {SalesUserRecord[] | null} The selected people, or null for an unknown team.
 */
function peopleFor(caller: SalesUserRecord, team: string | undefined): SalesUserRecord[] | null {
  const scoped = peopleInScope(caller)
  if (!team || team === 'ALL') return scoped
  const [kind, id] = team.split(':')
  const person = scoped.find((user) => user.id === id)
  if (!person || (kind === 'TEAM' && person.role === 'Sales Executive')) return null
  if (kind === 'USER') return [person]
  const ids = new Set(resolveDownlineUserIds(person.id, listSalesRoster()))
  return scoped.filter((user) => ids.has(user.id))
}

/**
 * @description The selected people with their assigned and achieved counts, in ranking order.
 * @param {Selection} selection - The validated selection.
 * @returns {{ user: SalesUserRecord; assigned: number; achieved: number }[]} The ranked people.
 */
export function rankedPeople(selection: Selection) {
  const targets = targetsFor(new Set(selection.people.map((u) => u.id)), selection.period, false)
  return selection.people
    .filter(isSeller)
    .map((user) => {
      const own = targets.filter((item) => item.target.ownerId === user.id)
      return { user, assigned: own.length, achieved: own.filter((item) => item.achieved).length }
    })
    .sort(compareRanking)
}
